/**
 * SyncManager - Background server synchronization with 200ms intervals
 * 
 * Provides reliable background synchronization while maintaining immediate
 * local operations for Word-class responsiveness.
 */

import { LocalDocumentEngine } from '../core/LocalDocumentEngine';
import { OperationalTransformEngine } from './OperationalTransformEngine';
import { OperationQueue } from './OperationQueue';
import { RetryManager } from './RetryManager';
import { TextToASTConverter } from '../services/TextToASTConverter';
import { DPIPersistenceService } from '../services/DPIPersistenceService';
import { 
  Operation, 
  RemoteOperation, 
  DEFAULT_CONFIG,
  InsertOperation,
  DeleteOperation
} from '../types/DocumentTypes';

// Enhanced backend integration types with HarfBuzz precision
export interface LayoutPlan {
  planHash: string;
  pageSetup?: {
    widthPx: number;
    heightPx: number;
    fontUsageHints?: any;
  };
  // Enhanced format properties (from HarfBuzz API specification)
  schemaVersion?: string;
  engineVersions?: {
    harfbuzz?: string;
    freetype?: string;
    icu?: string;
    fontSet?: string;
    service?: string;
  };
  pages: Array<{
    width: number;
    height: number;
    frames: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      lines: Array<{
        y: number;
        height: number;
        ascent?: number;
        descent?: number;
        baseline?: number;
        runs: Array<{
          x: number;
          width: number;
          text: string;
          fontFamily?: string;
          fontSize?: number;
          fontStyle?: string;
          fontWeight?: string;
          // HarfBuzz precision data
          positions?: Array<{
            gid: number;
            xAdvance: number;
            yAdvance: number;
            xOffset: number;
            yOffset: number;
          }>;
          charBoxes?: Array<{
            x: number;
            y: number;
            w: number;
            h: number;
            units: 'WORLD_PX' | 'DEVICE_PX' | 'FONT_UNITS';
            anchor: 'baseline' | 'topLeft';
          }>;
          glyphAdvances?: number[];
          // Enhanced HarfBuzz glyph data (from API specification)
          glyphs?: Array<{
            glyphId: number;
            advance: number;
            xOffset?: number;
            yOffset?: number;
          }>;
          totalAdvance?: number;
          totalHeight?: number;
          // Typography metadata
          scaleKind?: 'FONT_UNITS' | 'HB_26_6' | 'WORLD_PX';
          upem?: number;
          engineVersion?: string;
          engineVersions?: {
            harfbuzz: string;
            freetype: string;
            icu: string;
            fontSet: string;
            service: string;
          };
          fontSubstitutions?: Array<{
            requested: string;
            actual: string;
            reason: 'not_found' | 'fallback' | 'script_support';
            glyphRange: [number, number];
          }>;
          processingMetrics?: {
            shapingTime: number;
            cacheHit: boolean;
            fallbackUsed: boolean;
            glyphCount: number;
            characterCount: number;
          };
          // API versioning and caching
          schemaVersion?: string;
          cached?: boolean;
          cacheKey?: string;
          // Idempotency fields
          documentId?: string;
          paragraphId?: string;
          deltaSequence?: number;
        }>;
      }>;
    }>;
  }>;
  index?: {
    runById: Record<string, any>;
    blockById: Record<string, any>;
    pageById: Record<string, any>;
  };
  pageCount?: number;
  pageHashes?: string[];
  fontUsageHints?: any;
}

interface TypesetterResponse {
  success: boolean;
  data?: {
    layoutPlan: LayoutPlan;
    performance: {
      totalTime: number;
      shapingTime: number;
      layoutTime: number;
    };
  };
  error?: string;
}

interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncTime: number;
  pendingOperations: number;
  serverVersion: number;
  localVersion: number;
  conflictCount: number;
}

interface SyncResult {
  success: boolean;
  syncedOperations: Operation[];
  conflicts: any[];
  newServerVersion: number;
  error?: string;
}

export class SyncManager {
  private documentEngine!: LocalDocumentEngine;
  private otEngine!: OperationalTransformEngine;
  private operationQueue!: OperationQueue;
  private retryManager!: RetryManager;
  private eventEmitter: SyncEventEmitter = new SyncEventEmitter();
  
  // Enhanced LocalDocumentEngine coordination
  private harfbuzzBatchCoordination: boolean = false;
  private pendingLayoutRequests: Set<string> = new Set();
  private batchSyncTimeout: NodeJS.Timeout | null = null;
  
  // Sync state
  private syncState!: SyncState;
  private syncInterval: NodeJS.Timeout | null = null;
  private wsClient: WebSocketClient | null = null;
  
  // Enhanced backend integration with HarfBuzz microservice
  private astConverter: TextToASTConverter = new TextToASTConverter();
  private typesetterBaseUrl: string = '';
  private harfbuzzServiceUrl: string = '';
  private authToken: string = '';
  private currentLayoutPlan: LayoutPlan | null = null;
  private layoutPlanCache: Map<string, LayoutPlan> = new Map();
  
  // 🎯 RACE CONDITION FIX: Layout plan sequence tracking
  private layoutPlanSequence: number = 0;
  private lastAppliedSequence: number = -1;
  
  // Formatting information storage
  private documentFormatting: any[] = [];
  private blockHashMap: Map<string, string> = new Map();
  
  // Configuration
  private syncIntervalMs: number = DEFAULT_CONFIG.syncInterval;
  private maxBatchSize: number = 50;
  private syncTimeout: number = 5000;

  constructor(documentEngine?: LocalDocumentEngine, websocketUrl?: string, auth?: any) {
    if (!documentEngine) return; // Allow construction without args for useState
    this.documentEngine = documentEngine;
    this.otEngine = new OperationalTransformEngine();
    this.operationQueue = new OperationQueue();
    this.retryManager = new RetryManager();
    this.astConverter = new TextToASTConverter();
    
    this.syncState = {
      isOnline: false,
      isSyncing: false,
      lastSyncTime: 0,
      pendingOperations: 0,
      serverVersion: 0,
      localVersion: documentEngine.getVersion(),
      conflictCount: 0
    };
    
    if (websocketUrl) {
      this.setupWebSocketConnection(websocketUrl, auth);
    }
    this.setupDocumentListeners();
  }

  // ============================================================================
  // SYNC LIFECYCLE
  // ============================================================================

  /**
   * Start background synchronization loop
   */
  startSyncLoop(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    this.syncInterval = setInterval(() => {
      this.performSync();
    }, this.syncIntervalMs);
    
  }

  /**
   * Stop synchronization loop
   */
  stopSyncLoop(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    
  }

  /**
   * Perform immediate synchronization
   */
  async performSync(): Promise<SyncResult> {
    if (this.syncState.isSyncing || !this.syncState.isOnline) {
      return { 
        success: false, 
        syncedOperations: [], 
        conflicts: [], 
        newServerVersion: this.syncState.serverVersion,
        error: this.syncState.isSyncing ? 'Sync already in progress' : 'Offline'
      };
    }
    
    this.syncState.isSyncing = true;
    const startTime = performance.now();
    
    try {
      // Get pending operations
      const pendingOps = this.operationQueue.getPendingOperations(this.maxBatchSize);
      
      if (pendingOps.length === 0) {
        this.syncState.isSyncing = false;
        return { 
          success: true, 
          syncedOperations: [], 
          conflicts: [], 
          newServerVersion: this.syncState.serverVersion 
        };
      }
      
      
      // Send operations to server
      const response = await this.sendOperationsToServer(pendingOps);
      
      if (response.success) {
        // Handle successful sync
        const result = await this.handleSyncSuccess(response, pendingOps);
        
        const syncTime = performance.now() - startTime;
        
        return result;
      } else {
        // Handle sync failure
        return await this.handleSyncFailure(response, pendingOps);
      }
      
    } catch (error) {
      console.error('[SyncManager] Sync error:', error);
      return await this.handleSyncError(error as Error);
    } finally {
      this.syncState.isSyncing = false;
      this.syncState.lastSyncTime = Date.now();
    }
  }

  // ============================================================================
  // OPERATION QUEUING
  // ============================================================================

  /**
   * Queue operation for background sync
   */
  queueOperation(operation: Operation): void {
    this.operationQueue.enqueue(operation);
    this.syncState.pendingOperations = this.operationQueue.getPendingCount();
    
    
    // Trigger immediate sync for critical operations
    if (this.isCriticalOperation(operation)) {
      this.performSync();
    }
  }

  private isCriticalOperation(operation: Operation): boolean {
    // Critical operations that should sync immediately
    return (
      operation.type === 'delete' && 
      (operation as any).end - (operation as any).start > 100
    ); // Large deletions
  }

  // ============================================================================
  // SERVER COMMUNICATION
  // ============================================================================

  private async sendOperationsToServer(operations: Operation[]): Promise<ServerSyncResponse> {
    if (!this.wsClient) {
      throw new Error('WebSocket client not connected');
    }
    
    const payload = {
      operations,
      baseVersion: this.syncState.serverVersion,
      localVersion: this.syncState.localVersion,
      documentId: this.getDocumentId(),
      attorney: this.documentEngine.getState().attorney
    };
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Sync timeout'));
      }, this.syncTimeout);
      
      this.wsClient!.emit('sync-operations', payload, (response: ServerSyncResponse) => {
        clearTimeout(timeout);
        resolve(response);
      });
    });
  }

  private async handleSyncSuccess(
    response: ServerSyncResponse,
    syncedOperations: Operation[]
  ): Promise<SyncResult> {
    // Update server version
    this.syncState.serverVersion = response.newServerVersion;
    
    // Remove synced operations from queue
    this.operationQueue.markSynced(syncedOperations);
    
    // Handle remote operations
    if (response.remoteOperations && response.remoteOperations.length > 0) {
      await this.applyRemoteOperations(response.remoteOperations);
    }
    
    // Handle conflicts
    const conflicts = response.conflicts || [];
    if (conflicts.length > 0) {
      await this.handleConflicts(conflicts);
    }
    
    this.syncState.pendingOperations = this.operationQueue.getPendingCount();
    
    return {
      success: true,
      syncedOperations,
      conflicts,
      newServerVersion: response.newServerVersion
    };
  }

  private async handleSyncFailure(
    response: ServerSyncResponse,
    failedOperations: Operation[]
  ): Promise<SyncResult> {
    console.warn('[SyncManager] Sync failed:', response.error);
    
    // Queue operations for retry
    this.retryManager.queueForRetry(failedOperations, response.error || 'Unknown error');
    
    return {
      success: false,
      syncedOperations: [],
      conflicts: [],
      newServerVersion: this.syncState.serverVersion,
      error: response.error
    };
  }

  private async handleSyncError(error: Error): Promise<SyncResult> {
    console.error('[SyncManager] Sync error:', error);
    
    // Check if it's a network error
    if (this.isNetworkError(error)) {
      this.handleNetworkError();
    }
    
    return {
      success: false,
      syncedOperations: [],
      conflicts: [],
      newServerVersion: this.syncState.serverVersion,
      error: error.message
    };
  }

  // ============================================================================
  // REMOTE OPERATION HANDLING
  // ============================================================================

  private async applyRemoteOperations(remoteOps: RemoteOperation[]): Promise<void> {
    
    for (const remoteOp of remoteOps) {
      // Transform remote operation against local pending operations
      const pendingOps = this.operationQueue.getPendingOperations();
      const transformResult = this.otEngine.transformOperation(remoteOp as any, pendingOps as any);
      
      // Apply transformed operation to local document
      await this.applyTransformedOperation(transformResult.transformedOperation);
      
      // Handle any conflicts
      if (transformResult.conflicts.length > 0) {
        await this.handleConflicts(transformResult.conflicts);
      }
    }
  }

  private async applyTransformedOperation(operation: Operation): Promise<void> {
    // Apply operation to local document engine
    switch (operation.type) {
      case 'insert':
        this.documentEngine.insertCharacter((operation as any).position, (operation as any).content);
        break;
      case 'delete':
        const deleteOp = operation as any;
        this.documentEngine.deleteRange(deleteOp.start, deleteOp.end);
        break;
    }
    
  }

  // ============================================================================
  // CONFLICT HANDLING
  // ============================================================================

  private async handleConflicts(conflicts: any[]): Promise<void> {
    
    for (const conflict of conflicts) {
      // Try automatic resolution first
      if (conflict.autoResolvable) {
        await this.autoResolveConflict(conflict);
      } else {
        // Queue for manual resolution
        await this.queueForManualResolution(conflict);
      }
    }
  }

  private async autoResolveConflict(conflict: any): Promise<void> {
    // Implementation will depend on conflict resolution strategy
  }

  private async queueForManualResolution(conflict: any): Promise<void> {
    // Emit event for UI to show conflict resolution dialog
    this.emitConflictEvent(conflict);
  }

  // ============================================================================
  // NETWORK MANAGEMENT
  // ============================================================================

  private setupWebSocketConnection(websocketUrl: string, auth: any): void {
    // Placeholder for WebSocket setup
    
    // Will be implemented with actual WebSocket client
    this.syncState.isOnline = true; // Placeholder
  }

  private handleNetworkError(): void {
    this.syncState.isOnline = false;
    console.warn('[SyncManager] Network error detected, going offline');
    
    // Emit offline event
    this.emitNetworkEvent('offline');
    
    // Start reconnection attempts
    this.startReconnectionAttempts();
  }

  private startReconnectionAttempts(): void {
    const reconnectInterval = setInterval(() => {
      if (this.syncState.isOnline) {
        clearInterval(reconnectInterval);
        return;
      }
      
      // Try to reconnect
      this.attemptReconnection();
    }, 5000); // Try every 5 seconds
  }

  private attemptReconnection(): void {
    // Placeholder for reconnection logic
  }

  private isNetworkError(error: Error): boolean {
    return error.message.includes('network') || 
           error.message.includes('timeout') ||
           error.message.includes('connection');
  }

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  private setupDocumentListeners(): void {
    // 🔧 SMART CHANGE DETECTION: Disable automatic layout triggering
    // Layout requests are now handled by PaperCanvas smart change detection
    // Only queue operations for collaboration, don't trigger layout automatically
    
    this.documentEngine.on('operationCompleted', (data: { operation: Operation }) => {
      // DISABLED: Automatic layout updates - now handled by PaperCanvas smart detection
      // this.handleDocumentChange(); // DISABLED - handled by PaperCanvas
      
      // Conditionally queue collaboration operations when session is ready
      if ((this as any)._sessionReady) {
        this.queueOperation(data.operation);
      } else {
        // Defer collaboration queueing until session ready
        setTimeout(() => {
          if ((this as any)._sessionReady) {
            this.queueOperation(data.operation);
          }
        }, 50);
      }
    });

    // DISABLED: Additional document event listeners that trigger layout
    // Layout is now handled by PaperCanvas smart change detection
    /*
    this.documentEngine.on('text-changed', () => {
      this.handleDocumentChange();
    });

    this.documentEngine.on('insert', () => {
      this.handleDocumentChange();
    });

    this.documentEngine.on('delete', () => {
      this.handleDocumentChange();
    });
    */

    // Listen for collaboration session readiness
    try {
      window.addEventListener('edgedocs-session-ready', () => {
        (this as any)._sessionReady = true;
      }, { once: true });
    } catch {}
  }

  private emitConflictEvent(conflict: any): void {
    const event = new CustomEvent('edgedocs-conflict', { detail: conflict });
    window.dispatchEvent(event);
  }

  private emitNetworkEvent(status: 'online' | 'offline'): void {
    const event = new CustomEvent('edgedocs-network', { detail: { status } });
    window.dispatchEvent(event);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get current sync state
   */
  getSyncState(): SyncState {
    return { ...this.syncState };
  }

  /**
   * Force immediate sync
   */
  async forceSyncNow(): Promise<SyncResult> {
    return await this.performSync();
  }

  /**
   * Get sync statistics
   */
  getSyncStatistics(): {
    totalSyncs: number;
    successfulSyncs: number;
    failedSyncs: number;
    averageSyncTime: number;
    pendingOperations: number;
  } {
    return {
      totalSyncs: 0, // Will be implemented with actual tracking
      successfulSyncs: 0,
      failedSyncs: 0,
      averageSyncTime: 0,
      pendingOperations: this.syncState.pendingOperations
    };
  }

  /**
   * Set sync interval
   */
  setSyncInterval(intervalMs: number): void {
    this.syncIntervalMs = Math.max(100, intervalMs);
    
    if (this.syncInterval) {
      this.stopSyncLoop();
      this.startSyncLoop();
    }
    
  }

  /**
   * Enable/disable automatic sync
   */
  setAutoSync(enabled: boolean): void {
    if (enabled) {
      this.startSyncLoop();
    } else {
      this.stopSyncLoop();
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private getDocumentId(): string {
    try {
      const state: any = this.documentEngine.getState?.();
      return state?.documentId || state?.document_id || 'current-document-id';
    } catch {
      return 'current-document-id';
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy sync manager and cleanup resources
   */
  destroy(): void {
    this.stopSyncLoop();
    
    if (this.wsClient) {
      this.wsClient.disconnect();
    }
    
    this.operationQueue.clear();
    this.retryManager.clear();
    
  }

  // ============================================================================
  // INTEGRATION METHODS
  // ============================================================================

  async initialize(documentEngine: LocalDocumentEngine, websocketUrl: string, auth: any): Promise<void> {
    this.documentEngine = documentEngine;
    this.otEngine = new OperationalTransformEngine();
    this.operationQueue = new OperationQueue();
    this.retryManager = new RetryManager();
    
    this.syncState = {
      isOnline: false,
      isSyncing: false,
      lastSyncTime: 0,
      pendingOperations: 0,
      serverVersion: 0,
      localVersion: 1,
      conflictCount: 0
    };
    
    this.setupWebSocketConnection(websocketUrl, auth);
    this.setupDocumentListeners();
  }

  startBackgroundSync(): void {
  }

  stopBackgroundSync(): void {
  }

  on(event: string, callback: Function): void {
    this.eventEmitter.on(event, callback);
  }

  off(event: string, callback: Function): void {
    this.eventEmitter.off(event, callback);
  }

  emit(event: string, data: any): void {
    this.eventEmitter.emit(event, data);
  }

  // ============================================================================
  // ENHANCED BACKEND INTEGRATION
  // ============================================================================

  /**
   * Initialize enhanced backend integration with HarfBuzz microservice
   */
  initializeEnhancedBackend(baseUrl: string, token: string): void {
    this.typesetterBaseUrl = baseUrl;
    this.authToken = token;
    
    // REMOVED: Frontend HarfBuzz microservice URL - Backend handles all shaping
    // if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    //   this.harfbuzzServiceUrl = 'http://localhost:8081'; // Development
    // } else {
    //   this.harfbuzzServiceUrl = 'http://harfbuzz-microservice:8080'; // Production
    // }
    this.harfbuzzServiceUrl = ''; // Disabled - backend-only architecture
    // Enhanced document listeners are now set up in constructor
  }

  /**
   * REMOVED: HarfBuzz batch coordination - Backend handles all shaping
   */
  enableHarfBuzzCoordination(enable: boolean = true): void {
    // this.harfbuzzBatchCoordination = enable;
    this.harfbuzzBatchCoordination = false; // Force disabled - backend-only architecture
  }

  /**
   * Coordinate layout request with LocalDocumentEngine HarfBuzz batching
   */
  private async coordinateWithHarfBuzzBatch(requestId: string): Promise<void> {
    if (!this.harfbuzzBatchCoordination || !this.documentEngine) return;
    
    // Check if LocalDocumentEngine has pending HarfBuzz operations
    const engineState = this.documentEngine.getReactState();
    const harfbuzzMetrics = this.documentEngine.getHarfBuzzMetricsSync();
    
    if (harfbuzzMetrics.pendingRequests > 0) {
      
      // Wait for HarfBuzz batch to complete before requesting layout
      this.pendingLayoutRequests.add(requestId);
      
      // Set up coordination timeout
      if (this.batchSyncTimeout) {
        clearTimeout(this.batchSyncTimeout);
      }
      
      this.batchSyncTimeout = setTimeout(async () => {
        // Process pending layout requests after HarfBuzz batch completion
        if (this.pendingLayoutRequests.has(requestId)) {
          this.pendingLayoutRequests.delete(requestId);
          await this.requestLayoutPlan(true);
        }
      }, 100); // Short delay to allow HarfBuzz batch completion
    }
  }

  /**
   * Enhanced layout request with HarfBuzz coordination
   */
  async requestLayoutPlanCoordinated(forceRefresh: boolean = false): Promise<LayoutPlan | null> {
    const requestId = `layout-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Coordinate with HarfBuzz batching if enabled
    if (this.harfbuzzBatchCoordination) {
      await this.coordinateWithHarfBuzzBatch(requestId);
    }
    
    return this.requestLayoutPlan(forceRefresh);
  }

  /**
   * Request layout plan from enhanced backend typesetter
   */
  async requestLayoutPlan(forceRefresh: boolean = false): Promise<LayoutPlan | null> {
    // Minimal request logging to avoid noise
    
    if (!this.typesetterBaseUrl || !this.authToken) {
      console.warn('[SyncManager] Enhanced backend not initialized');
      return null;
    }

    // 🔧 SMART CHANGE DETECTION: Disable aggressive aborting since smart detection prevents rapid concurrent requests
    // With smart change detection, we should rarely have concurrent requests, so aborting is counterproductive
    if (this.currentAbortController && this.layoutRequestInFlight) {
      console.log('⚠️ [SMART_ABORT] Previous request still in flight - waiting instead of aborting');
      // Instead of aborting, wait for the current request to complete
      return null; // Let the current request finish
    }

    // Create new abort controller for this request
    this.currentAbortController = new AbortController();

    try {
      const currentText = this.documentEngine.getText();
      
      
      if (!forceRefresh && this.layoutRequestInFlight) {
        return null;
      }
      // Check if we have formatting information available
      const formattingInfo = this.getDocumentFormatting();
      let astResult;
      
      if (formattingInfo && formattingInfo.length > 0) {
        astResult = this.astConverter.convertFormattedTextToAST(currentText, formattingInfo);
      } else {
        astResult = this.astConverter.convertTextToAST(currentText);
      }
      
      
      if (!astResult.success || !astResult.ast) {
        console.error('[SyncManager] Failed to convert text to AST');
        return null;
      }

      // Check cache first (unless forced refresh)
      const astHash = this.generateHash(JSON.stringify(astResult.ast));
      if (!forceRefresh && this.layoutPlanCache.has(astHash)) {
        // TEMPORARY: Always force refresh to test line breaking fix
        // return this.layoutPlanCache.get(astHash)!;
      }

      // Paragraph-scoped diffing: compute block hashes, include only changed
      const changedBlocks = [] as any[];
      const newHashMap: Map<string, string> = new Map();
      const blocks = astResult.ast.blocks || [];
      for (const block of blocks as any[]) {
        const serialized = JSON.stringify(block);
        const hash = this.generateHash(serialized);
        const blockKey = (block && (block.id || block.blockId)) ? (block.id || block.blockId) : this.generateHash((block?.type || 'block') + serialized);
        newHashMap.set(blockKey, hash);
        const prev = this.blockHashMap.get(blockKey);
        if (forceRefresh || !prev || prev !== hash) {
          changedBlocks.push(block);
        }
      }
      if (!forceRefresh && changedBlocks.length === 0) {
        return null; // nothing changed; skip
      }
      const scopedAst = { ...astResult.ast, blocks: forceRefresh ? blocks : changedBlocks };
      const payload = {
        ast: scopedAst,
        styles: this.getDefaultStyles(),
        pageSetup: this.getBackendPageSetup()
      };



      
      // Use new HarfBuzz microservice for layout requests
      // HarfBuzz microservice provides: native HarfBuzz + FreeType, batch processing, font manifest integration
      const response = await fetch(`${this.typesetterBaseUrl}/api/v1/edgedocs/typesetter/layout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
          'X-Contract-Version': '1.0.0'
        },
        body: JSON.stringify(payload),
        signal: this.currentAbortController?.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: any = await response.json();
      
      
      // Backend returns { status: "ok", data: { layoutPlan: ... }, errors: [] }
      if (result.status !== 'ok' || !result.data?.layoutPlan) {
        const errorMsg = result.errors?.[0]?.message || result.errors?.[0]?.code || 'Invalid response from typesetter';
        throw new Error(errorMsg);
      }
      
      // Validate HarfBuzz API contract compliance
      this.validateHarfBuzzContract(result.data.layoutPlan);

      const rawLayoutPlan = result.data.layoutPlan;
      
      // Transform backend response to match frontend expectations
      const layoutPlan = this.transformBackendLayoutPlan(rawLayoutPlan);
      
      
      // Cache the layout plan
      this.layoutPlanCache.set(astHash, layoutPlan);
      this.currentLayoutPlan = layoutPlan;
      // Update block hashes on success
      this.blockHashMap = newHashMap;
      
      
      
      // 🎯 RACE CONDITION FIX: Add sequence number to prevent out-of-order layout plan application
      const currentSequence = ++this.layoutPlanSequence;
      
      this.emit('layout-plan-updated', { 
        layoutPlan, 
        performance: result.data.performance,
        sequence: currentSequence 
      });
      
      return layoutPlan;
      
    } catch (error) {
      console.error('[SyncManager] Failed to request layout plan:', error);
      this.emit('layout-plan-error', { error: error instanceof Error ? error.message : String(error) });
      return null;
    }
    finally {
      this.layoutRequestInFlight = false;
    }
  }

  /**
   * Get current layout plan
   */
  getCurrentLayoutPlan(): LayoutPlan | null {
    return this.currentLayoutPlan;
  }

  /**
   * Trigger layout plan refresh on document changes
   */
  private async handleDocumentChange(): Promise<void> {
    // 🎯 SMART DEBOUNCING: Only cancel if we're about to make a new request
    // Don't cancel immediately - let current requests complete if they're close to finishing
    
    // Debounce layout plan requests to avoid excessive API calls
    if (this.layoutRefreshTimeout) {
      clearTimeout(this.layoutRefreshTimeout);
    }

    this.layoutRefreshTimeout = setTimeout(async () => {
      // Only cancel if there's still a request in flight after debounce period
      if (this.currentAbortController && this.layoutRequestInFlight) {
        this.currentAbortController.abort();
        console.log('🔧 [SMART_CANCEL] Cancelled stale layout request after debounce');
      }
      
      // Clear cache only when actually making a new request
      this.layoutPlanCache.clear();
      
      // Use coordinated layout request for better HarfBuzz integration
      await this.requestLayoutPlanCoordinated(true); // Force refresh to include all current text
    }, 150); // Faster debounce for responsive typing
  }

  private layoutRefreshTimeout: NodeJS.Timeout | null = null;
  private currentAbortController: AbortController | null = null;
  private layoutRequestInFlight: boolean = false;
  


  /**
   * Get default styles for typesetter (updated for HarfBuzz microservice)
   */
  private getDefaultStyles() {
    return {
      paragraphStyles: {
        Normal: { 
          font: this.currentFontSettings.fontFamily,  // Use current font family
          size: this.currentFontSettings.fontSize,    // Use current font size
          lineHeight: 1.4,  // Match working format
          align: 'start'
        }
      },
      characterStyles: {}  // Match working format - empty object
    };
  }

  /**
   * Get default page setup for typesetter (matches backend schema)
   * DPI is auto-detected and persisted to maintain user preferences
   */
  private currentPageSetup = {
    pageSize: 'Letter' as const,  // Match working format
    dpi: DPIPersistenceService.getDPI(),  // ✅ DPI FIX: Auto-detect and load actual DPI
    margins: this.getDefaultMargins(DPIPersistenceService.getDPI()), // DPI-aware margins
    columns: 1,
    hyphenation: false,
    bidi: 'auto' as const,
    orientation: 'portrait' as const
  };

  /**
   * Get default 0.25-inch margins for any DPI
   */
  private getDefaultMargins(dpi: number) {
    return {
      top: Math.round(dpi * 0.25),    // 0.25" = 24px at 96 DPI
      right: Math.round(dpi * 0.25),  // 0.25" = 24px at 96 DPI
      bottom: Math.round(dpi * 0.25), // 0.25" = 24px at 96 DPI
      left: Math.round(dpi * 0.25)    // 0.25" = 24px at 96 DPI
    };
  }

  private getDefaultPageSetup() {
    return {
      ...this.currentPageSetup,
      fontFamily: this.currentFontSettings.fontFamily,
      fontSize: this.currentFontSettings.fontSize,
      fontColor: this.currentFontSettings.fontColor
    };
  }

  public getBackendPageSetup() {
    // Return the fields that the backend expects (updated format with typography)
    return {
      pageSize: this.currentPageSetup.pageSize,
      dpi: this.currentPageSetup.dpi,
      margins: this.currentPageSetup.margins,
      orientation: this.currentPageSetup.orientation,
      typography: {
        fontFamily: this.currentFontSettings.fontFamily,
        fontSize: this.currentFontSettings.fontSize,
        fontWeight: 400,
        fontStyle: 'normal',
        fontColor: this.currentFontSettings.fontColor,
        lineHeight: 1.4
      }
    };
  }

  /**
   * Update page setup and trigger layout refresh
   */
  public async updatePageSetup(newPageSetup: any) {
    
    
    // Extract font settings from typography object or direct properties (backward compatibility)
    const { fontFamily, fontSize, fontColor, typography, ...pageSetupOnly } = newPageSetup;
    const fontSettings = typography || { fontFamily, fontSize, fontColor };
    
    // Update page setup (DPI, margins, etc.)
    
    // Create updated page setup with explicit margin handling
    const updatedPageSetup = { ...this.currentPageSetup, ...pageSetupOnly };
    
    // 🔧 MARGIN FIX: Preserve explicit margins from Page Setup dialog
    if (newPageSetup.margins) {
      updatedPageSetup.margins = newPageSetup.margins;
      console.log('🔧 [MARGIN_FIX] Using explicit margins from Page Setup dialog');
    }
    
    // 💾 PERSIST DPI: Save DPI changes as document metadata (like font settings)
    const dpiChanged = newPageSetup.dpi && newPageSetup.dpi !== this.currentPageSetup.dpi;
    if (dpiChanged) {
      console.log('💾 [DPI_PERSISTENCE] DPI changed, saving to persistence:', newPageSetup.dpi);
      DPIPersistenceService.saveDPI(newPageSetup.dpi);
    }
    
    this.currentPageSetup = updatedPageSetup;
    
    // Update font settings in styles if provided
    if (fontSettings.fontFamily || fontSettings.fontSize || fontSettings.fontColor) {
      this.updateFontSettings(fontSettings);
    }
    
    // 🚀 FIX: Don't clear cache immediately - preserve current plan during font changes
    // Store current plan as fallback during transition
    const fallbackPlan = this.currentLayoutPlan;
    
    // Cancel any pending layout refresh from document changes
    if (this.layoutRefreshTimeout) {
      clearTimeout(this.layoutRefreshTimeout);
      this.layoutRefreshTimeout = null;
    }
    
    console.log('🔄 [FONT_CHANGE_FIX] Requesting new layout plan while preserving current plan as fallback');
    
    try {
      // Request new layout with updated page setup (force refresh)
      const newLayoutPlan = await this.requestLayoutPlan(true);
      
      if (newLayoutPlan) {
        // Only clear cache after successful new plan retrieval
        this.layoutPlanCache.clear();
        this.blockHashMap.clear();
        console.log('✅ [FONT_CHANGE_FIX] Successfully updated to new layout plan');
        return newLayoutPlan;
      } else {
        console.warn('⚠️ [FONT_CHANGE_FIX] New layout plan failed, keeping current plan');
        return fallbackPlan;
      }
    } catch (error) {
      console.error('❌ [FONT_CHANGE_FIX] Layout plan request failed, keeping current plan:', error);
      return fallbackPlan;
    }
  }

  private currentFontSettings = {
    fontFamily: 'Times New Roman',
    fontSize: 12, // points
    fontColor: '#000000'
  };

  /**
   * Update font settings in styles
   */
  private updateFontSettings(fontSettings: { fontFamily?: string; fontSize?: number; fontColor?: string }) {
    if (fontSettings.fontFamily) {
      this.currentFontSettings.fontFamily = fontSettings.fontFamily;
    }
    if (fontSettings.fontSize) {
      this.currentFontSettings.fontSize = fontSettings.fontSize;
    }
    if (fontSettings.fontColor) {
      this.currentFontSettings.fontColor = fontSettings.fontColor;
    }
    
  }

  /**
   * Get current page setup (including font settings for UI)
   */
  public getCurrentPageSetup() {
    return { 
      ...this.currentPageSetup,
      ...this.currentFontSettings
    };
  }

  /**
   * Store formatting information from rich text paste
   */
  setDocumentFormatting(formatting: any[]): void {
    this.documentFormatting = formatting;
  }

  /**
   * Get current document formatting information
   */
  getDocumentFormatting(): any[] {
    return this.documentFormatting;
  }

  /**
   * Clear formatting information
   */
  clearDocumentFormatting(): void {
    this.documentFormatting = [];
  }

  /**
   * Validate HarfBuzz API contract compliance
   */
  private validateHarfBuzzContract(layoutPlan: LayoutPlan): void {
    
    // Check for schema version in runs
    let hasSchemaVersion = false;
    let hasEngineVersions = false;
    let fontSubstitutionCount = 0;
    
    for (const page of layoutPlan.pages || []) {
      for (const frame of page.frames || []) {
        for (const line of frame.lines || []) {
          for (const run of line.runs || []) {
            if (run.schemaVersion) {
              hasSchemaVersion = true;
              if (run.schemaVersion !== '1.0') {
                console.warn('🚨 [HarfBuzz Contract] Unexpected schema version:', run.schemaVersion);
              }
            }
            
            if (run.engineVersions) {
              hasEngineVersions = true;
              console.log('🔧 [HarfBuzz Contract] Engine versions detected:', {
                harfbuzz: run.engineVersions.harfbuzz,
                freetype: run.engineVersions.freetype,
                fontSet: run.engineVersions.fontSet
              });
            }
            
            if (run.fontSubstitutions && run.fontSubstitutions.length > 0) {
              fontSubstitutionCount += run.fontSubstitutions.length;
            }
          }
        }
      }
    }
    
    console.log('✅ [HarfBuzz Contract] Validation complete:', {
      hasSchemaVersion,
      hasEngineVersions,
      fontSubstitutionCount,
      planHash: layoutPlan.planHash?.slice(0, 8)
    });
  }

  /**
   * Generate hash for caching
   */
  private generateHash(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  /**
   * Clear layout plan cache
   */
  clearLayoutCache(): void {
    this.layoutPlanCache.clear();
    this.currentLayoutPlan = null;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; currentPlan: boolean } {
    return {
      size: this.layoutPlanCache.size,
      currentPlan: this.currentLayoutPlan !== null
    };
  }

  /**
   * Transform backend layout plan structure to match frontend expectations
   */
  private transformBackendLayoutPlan(rawPlan: any): any {
    // No transformation needed - backend provides nested structure that frontend can use directly
    return rawPlan;
  }
}

// ============================================================================
// SUPPORTING INTERFACES
// ============================================================================

interface ServerSyncResponse {
  success: boolean;
  newServerVersion: number;
  remoteOperations?: RemoteOperation[];
  conflicts?: any[];
  error?: string;
}

interface WebSocketClient {
  emit(event: string, data: any, callback?: (response: any) => void): void;
  disconnect(): void;
}

// ============================================================================
// SYNC EVENTS
// ============================================================================

export class SyncEventEmitter {
  private listeners: Map<string, Function[]> = new Map();

  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  emit(event: string, data?: any): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      for (const callback of callbacks) {
        try {
          callback(data);
        } catch (error) {
          console.error(`[SyncEventEmitter] Error in ${event} listener:`, error);
        }
      }
    }
  }

  off(event: string, callback: Function): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }


}
